import {
  Button,
  DatePicker,
  Flex,
  Form,
  message,
  Select,
  Slider,
  Spin,
  TimePicker,
  Typography,
} from 'antd'
import { ClearOutlined, SaveOutlined } from '@ant-design/icons'
import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import axios from 'axios'
import { API_BASE_URL, GET_API_TOKEN } from '../../../../constants/apiConstants'

const { Text } = Typography

export type AddsFormPropsType = {
  patientKey: string
}

export type UrineOutputDataType = {
  periodFrom: Dayjs
  periodTo: Dayjs
  value: string
}

export type FormDataType = {
  createTimestamp: number
  painLevel: number
  consciousness: string
  fourHourUrineOutput: string
  urineOutputTimestamp: number
}

export type ErrorsType = {
  pain: string | null
  urineOutputTime: string | null
  urineOutput: string | null
}

export type IsFieldUpdatedType = {
  pain: boolean
  urineOutputTime: boolean
}

const formColStyle = {
  padding: '10px 40px',
}

const dateTimePickerStyle = {
  padding: '10px',
  margin: '0 0 10px 0',
  width: '100%',
}

const selectFieldStyle = {
  width: '100%',
  height: '43px',
}

const buttonStyle = {
  width: '200px',
  height: 'fit-content',
  padding: '10px 20px',
  marginRight: '10px',
}

const InitialUrineOutputData: UrineOutputDataType = {
  periodFrom: dayjs().subtract(4, 'hours'),
  periodTo: dayjs(),
  value: '',
}

const InitialErrors: ErrorsType = {
  pain: null,
  urineOutputTime: null,
  urineOutput: null,
}

const InitialIsFieldUpdated: IsFieldUpdatedType = {
  pain: false,
  urineOutputTime: false,
}

const AddsForm = ({ patientKey }: AddsFormPropsType) => {
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pain, setPain] = useState(0)
  const [isFieldUpdated, setIsFieldUpdated] = useState<IsFieldUpdatedType>(
    InitialIsFieldUpdated
  )
  const [consciousness, setConsciousness] = useState('')
  const [urineOutputData, setUrineOutputData] = useState<UrineOutputDataType>(
    InitialUrineOutputData
  )
  const [errors, setErrors] = useState<ErrorsType>(InitialErrors)

  const onPainChange = (value: number): void => {
    if (!isFieldUpdated.pain)
      setIsFieldUpdated((prev) => ({ ...prev, pain: true }))
    setPain(value)
  }

  const onConsciousnessChange = (value: string): void => {
    setConsciousness(value === 'Select' ? '' : value)
  }

  const onPeriodFromChange = (value: Dayjs | null, isDate: boolean): void => {
    if (!isFieldUpdated.urineOutputTime)
      setIsFieldUpdated((prev) => ({ ...prev, urineOutputTime: true }))

    setUrineOutputData((prevData) => {
      if (!value) return prevData

      let newPeriodFrom = dayjs(prevData.periodFrom)

      if (isDate) {
        newPeriodFrom = newPeriodFrom
          .year(value.year())
          .month(value.month())
          .date(value.date())
      } else {
        newPeriodFrom = newPeriodFrom.hour(value.hour()).minute(value.minute())
      }

      const newPeriodTo = newPeriodFrom.add(4, 'hours')

      if (newPeriodFrom.isAfter(dayjs()) || newPeriodTo.isAfter(dayjs())) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          urineOutputTime: 'Urine Output time cannot be in the future',
        }))
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          urineOutputTime: null,
        }))
      }

      return {
        ...prevData,
        periodFrom: newPeriodFrom,
        periodTo: newPeriodTo,
      }
    })
  }

  const onPeriodToChange = (value: Dayjs | null, isDate: boolean): void => {
    if (!isFieldUpdated.urineOutputTime)
      setIsFieldUpdated((prev) => ({ ...prev, urineOutputTime: true }))

    setUrineOutputData((prevData) => {
      if (!value) return prevData

      let newPeriodTo = dayjs(prevData.periodTo)

      if (isDate) {
        newPeriodTo = newPeriodTo
          .year(value.year())
          .month(value.month())
          .date(value.date())
      } else {
        newPeriodTo = newPeriodTo.hour(value.hour()).minute(value.minute())
      }

      const newPeriodFrom = newPeriodTo.subtract(4, 'hours')

      if (newPeriodFrom.isAfter(dayjs()) || newPeriodTo.isAfter(dayjs())) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          urineOutputTime: 'Urine Output time cannot be in the future',
        }))
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          urineOutputTime: null,
        }))
      }

      return {
        ...prevData,
        periodTo: newPeriodTo,
        periodFrom: newPeriodFrom,
      }
    })
  }

  const onUrineOutputChange = (value: string): void => {
    setUrineOutputData((prevData) => ({
      ...prevData,
      value: value === 'Select' ? '' : value,
    }))
  }

  const onClearForm = () => {
    form.resetFields()
    setPain(0)
    setConsciousness('')
    setUrineOutputData(InitialUrineOutputData)
    setErrors(InitialErrors)
    setIsFieldUpdated(InitialIsFieldUpdated)
  }

  const isDataValid = (): boolean => {
    let clear = true
    if (!isFieldUpdated.pain) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        pain: 'Pain level is 0 - Click Save again to continue if correct',
      }))
      setIsFieldUpdated((prev) => ({ ...prev, pain: true }))
      clear = false
    }

    if (isFieldUpdated.urineOutputTime && urineOutputData.value === '') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        urineOutput:
          'No Value selected for Urine Output - Click Save again to continue if correct',
      }))
      setIsFieldUpdated((prev) => ({ ...prev, urineOutputTime: false }))
      clear = false
    }

    if (errors.urineOutputTime) {
      clear = false
    }

    return clear
  }

  const onFormSubmit = async () => {
    if (!isDataValid()) return

    try {
      setErrors(InitialErrors)
      setIsFieldUpdated(InitialIsFieldUpdated)
      setIsSubmitting(true)
      const formData = {
        createTimestamp: new Date().valueOf(),
        patientKey: patientKey,
        painLevel: pain,
        consciousness: consciousness,
        fourHourUrineOutput: urineOutputData.value,
        urineOutputTimestamp: urineOutputData.periodTo.valueOf(),
      }

      const response = await axios({
        method: 'post',
        baseURL: API_BASE_URL,
        url: '/api/rpmweb/adds/manual',
        headers: {
          Authorization: `Bearer ${GET_API_TOKEN()}`,
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(formData),
      })

      const data = response.data

      if (response.status === 200) {
        message.success('Data added successfully!')
      } else {
        message.error(data.statusText || 'Error adding data')
      }
    } catch (error) {
      message.error('Error connecting to the server')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFormSubmit}
      disabled={isSubmitting}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
      }}
      layout='vertical'
    >
      <Flex>
        <Flex flex={1} style={formColStyle} vertical>
          <Form.Item name='pain' label='Pain Level (0 - 10):'>
            <Flex vertical>
              <Slider
                defaultValue={0}
                min={0}
                max={10}
                onChange={onPainChange}
              />
              <Text type='warning'>{errors.pain}</Text>
            </Flex>
          </Form.Item>
          <Form.Item
            name='consc'
            label='Consciousness:'
            rules={[
              { required: true, message: 'Please select a consciousness' },
            ]}
          >
            <Select
              style={selectFieldStyle}
              onChange={onConsciousnessChange}
              placeholder='Select consciousness'
              options={[
                { value: 'Alert', label: 'Alert' },
                { value: 'Voice', label: 'Voice' },
                { value: 'Pain', label: 'Pain' },
                { value: 'Unconscious', label: 'Unconscious' },
              ]}
            />
          </Form.Item>
        </Flex>
        <Flex flex={1} style={formColStyle} vertical>
          <Form.Item label='4-Hour Urine Output Time:'>
            <Flex>
              <DatePicker
                name='periodFromDate'
                format='DD/MM/YYYY'
                disabledDate={(current) => current && current.isAfter(dayjs())}
                style={{ ...dateTimePickerStyle, marginRight: '10px' }}
                value={urineOutputData.periodFrom}
                onChange={(value, _) => onPeriodFromChange(value, true)}
              />
              <TimePicker
                name='periodFromTime'
                use12Hours
                format='h:mm A'
                style={dateTimePickerStyle}
                value={urineOutputData.periodFrom}
                onChange={(value, _) => onPeriodFromChange(value, false)}
              />
            </Flex>
            <Flex>
              <DatePicker
                name='periodToDate'
                format='DD/MM/YYYY'
                disabledDate={(current) => current && current.isAfter(dayjs())}
                style={{ ...dateTimePickerStyle, marginRight: '10px' }}
                value={urineOutputData.periodTo}
                onChange={(value, _) => onPeriodToChange(value, true)}
              />
              <TimePicker
                name='periodToTime'
                use12Hours
                format='h:mm A'
                style={dateTimePickerStyle}
                value={urineOutputData.periodTo}
                onChange={(value, _) => onPeriodToChange(value, false)}
              />
            </Flex>
            <Text type='danger'>{errors.urineOutputTime}</Text>
          </Form.Item>
          <Form.Item name='urineOutput' label='4-Hour Urine Output (ml):'>
            <Flex vertical>
              <Select
                style={selectFieldStyle}
                defaultValue={'Select'}
                onChange={onUrineOutputChange}
                options={[
                  { value: 'Select', label: 'Select...' },
                  { value: '>= 800', label: '>= 800' },
                  { value: '120-799', label: '120-799' },
                  { value: '80-119', label: '80-119' },
                  { value: '<= 79>', label: '<= 79>' },
                ]}
              />
              <Text type='warning'>{errors.urineOutput}</Text>
            </Flex>
          </Form.Item>
        </Flex>
      </Flex>

      {isSubmitting ? (
        <Spin />
      ) : (
        <Flex justify='center' style={formColStyle}>
          <Button
            type='primary'
            htmlType='submit'
            icon={<SaveOutlined />}
            style={buttonStyle}
          >
            Save
          </Button>
          <Button
            icon={<ClearOutlined />}
            onClick={onClearForm}
            style={buttonStyle}
          >
            Clear
          </Button>
        </Flex>
      )}
    </Form>
  )
}

export default AddsForm
